import React from 'react';
import { Link } from 'react-router';

/**
 * A model number, as a way into the Equipment Catalog.
 *
 * A model number is the one string that ties a device record to what the
 * manufacturer published about it, so wherever the console prints one it
 * should be possible to get from it to the catalog listing — the documents,
 * the motor family, the rated static pressure — without retyping it into a
 * search box.
 *
 * There are two destinations, and which one is used says something in itself:
 *
 * - the record matched a catalog model, so the link opens that listing;
 * - it matched nothing, so the link runs the catalog search for the model
 *   number. The operator lands on the answer to "do we hold anything for
 *   this?" — usually "no", which is worth knowing and is the first step to
 *   fixing it.
 *
 * Never used inside an editable field: a model number an operator is in the
 * middle of typing is not a link, and navigating away from a half-filled form
 * loses their work.
 */
export interface ModelLinkProps {
  /** The model number as the record holds it. */
  model: string | null | undefined;
  /**
   * The catalog row the record matched, when it matched one.
   *
   * A string as well as a number, because that is what arrives: the catalog
   * id is a Postgres bigint and the device search hands it back as `"3"`,
   * while the single-device read can hand back `3`. Anything that is not a
   * positive whole number is treated as no match — a link to `?model_id=NaN`
   * would open nothing at all, where the search at least says whether the
   * catalog holds this model.
   */
  catalogModelId?: number | string | null;
  /**
   * Open the catalog in a new tab instead of navigating there.
   *
   * For callers that hold something the operator would lose: the equipment
   * editor is a form of unsaved corrections, and a model number in it is a
   * value being checked against the catalog, not a way out of the form.
   */
  newTab?: boolean;
  /** Caller styling, carried onto the link and onto the fallback alike. */
  className?: string;
}

const OPEN_TITLE = 'Open this model in the Equipment Catalog';
const SEARCH_TITLE = 'Search the Equipment Catalog for this model';
const NEW_TAB_OPEN_TITLE = 'Open this model in the Equipment Catalog in a new tab';
const NEW_TAB_SEARCH_TITLE = 'Search the Equipment Catalog for this model in a new tab';

/** A catalog id from either shape, or null when it is not one. */
const catalogId = (raw: number | string | null | undefined): number | null => {
  if (raw === null || raw === undefined) return null;
  const text = String(raw).trim();
  if (!/^\d+$/.test(text)) return null;
  const id = Number(text);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const ModelLink: React.FC<ModelLinkProps> = ({
  model,
  catalogModelId,
  newTab,
  className,
}) => {
  const text = (model ?? '').trim();
  if (text.length === 0) return <span className={className}>—</span>;

  const id = catalogId(catalogModelId);
  const to =
    id !== null
      ? `/equipment-catalog?model_id=${id}`
      : `/equipment-catalog?q=${encodeURIComponent(text)}`;
  const style = ['text-blue-700 hover:underline', className].filter(Boolean).join(' ');

  // A plain anchor for the new tab: a router Link would still be handled by
  // the router in the tab it was clicked in, which is the thing being avoided.
  if (newTab) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        title={id !== null ? NEW_TAB_OPEN_TITLE : NEW_TAB_SEARCH_TITLE}
        className={style}
      >
        {text}
      </a>
    );
  }

  return (
    <Link to={to} title={id !== null ? OPEN_TITLE : SEARCH_TITLE} className={style}>
      {text}
    </Link>
  );
};

export default ModelLink;
